import React, { forwardRef, memo, useCallback, useMemo } from 'react'

import {
  BaseCitationItem,
  BaseSourceCitation,
  CitationTriggerWrapper,
} from '@/components/chat-v2/ui/MarkdownText'
import { CompactThumbnail } from '@/components/common/Attachment/Thumbnail'
import Spinner from '@/components/common/Spinner'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/common/TooltipV3'
import {
  useWikiCitationFilesById,
  useWikiCitationPursuitFilesLoaded,
  useWikiCitationSetActiveSourceIds,
  useWikiCitationSourceFileNameById,
  useWikiCitationSources,
  useWikiDeletedSourceFileIdSet,
  useWikiOpenSourceViewer,
} from '@/components/pursuits/wiki/ui/context'
import {
  getWikiSourceFileLabel,
  isDeletedWikiSourceFile,
} from '@/components/pursuits/wiki/ui/sourceFiles'

import { cleanFileName } from '@/helpers/file'

import type { Source } from '@/types/chat-v2'
import type { File } from '@/types/File'
import type { UUID } from '@/types/Id'

const hasStoredWikiFileLabel = (
  source: Source | undefined,
  sourceFileNameById: Partial<Record<UUID, string>>,
): boolean => {
  if (source?.source_type !== 'file') return false
  if (source.file_name?.trim()) return true
  return Boolean(sourceFileNameById[source.file_id]?.trim())
}

const getCitationDisplayName = ({
  source,
  file,
  deletedFileIds,
  sourceFileNameById,
}: {
  source: Source | null | undefined
  file: File | null | undefined
  deletedFileIds: ReadonlySet<UUID>
  sourceFileNameById: Partial<Record<UUID, string>>
}): string | null => {
  if (!source) return null

  if (source.source_type === 'file') {
    return cleanFileName(
      getWikiSourceFileLabel({
        fileId: source.file_id,
        file: file ?? null,
        sourceFileNameById,
        deletedSourceFileIds: deletedFileIds,
      }),
    )
  }

  if (source.source_type === 'web') {
    return source.url
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .split('/')[0]
  }

  if (source.source_type === 'tool') {
    return source.tool_display_name ?? source.tool_name
  }

  return null
}

const formatIdRange = (ids: number[]): string => {
  if (ids.length === 0) return ''
  if (ids.length === 1) return `[${ids[0]}]`
  const sorted = [...ids].sort((a, b) => a - b)
  return `[${sorted[0]}-${sorted[sorted.length - 1]}]`
}

const formatFileCitationMetadata = (source: Source | null | undefined) => {
  if (!source || source.source_type !== 'file') return []

  const metadata: string[] = []
  if (source.is_page_image) {
    metadata.push('Image')
  } else if (source.file_page !== undefined) {
    metadata.push(`Page ${source.file_page}`)
  }

  if (source.line_range && source.line_range[0] !== source.line_range[1]) {
    metadata.push(`Lines ${source.line_range[0]}-${source.line_range[1]}`)
  } else if (source.line_range) {
    metadata.push(`Line ${source.line_range[0]}`)
  }

  return metadata
}

interface CitationGroup {
  fileId: UUID | null
  ids: number[]
  sourceType:
    | 'file'
    | 'web'
    | 'entity'
    | 'image'
    | 'tool'
    | 'pursuit'
    | 'explore'
}

const groupConsecutiveCitationsByFile = (
  ids: number[],
  sources: Record<number, Source>,
): CitationGroup[] => {
  if (ids.length === 0) return []

  const groups: CitationGroup[] = []
  let currentGroup: CitationGroup | null = null

  for (const id of ids) {
    const source = sources[id]
    if (!source) continue

    const isFile = source.source_type === 'file'
    const fileId = isFile ? (source.file_id as UUID) : null
    const sourceType = source.source_type

    const canGroupWithCurrent =
      currentGroup != null &&
      currentGroup.sourceType === 'file' &&
      isFile &&
      currentGroup.fileId === fileId

    if (canGroupWithCurrent && currentGroup) {
      currentGroup.ids.push(id)
      continue
    }

    if (currentGroup) {
      groups.push(currentGroup)
    }

    currentGroup = { fileId, ids: [id], sourceType }
  }

  if (currentGroup) {
    groups.push(currentGroup)
  }

  return groups
}

interface FirstCitationNameProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'children'
> {
  sourceIds: number[]
  idRange: string
}

const NoopButton = forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ ...props }, ref) => {
  return (
    <button
      ref={ref}
      type="button"
      style={{ display: 'none' }}
      aria-hidden="true"
      {...props}
    />
  )
})

NoopButton.displayName = 'Wiki.NoopButton'

const WikiFirstCitationName = forwardRef<
  HTMLButtonElement,
  FirstCitationNameProps
>(({ sourceIds, idRange, ...props }, ref) => {
  const sources = useWikiCitationSources()
  const filesById = useWikiCitationFilesById()
  const sourceFileNameById = useWikiCitationSourceFileNameById()
  const deletedFileIds = useWikiDeletedSourceFileIdSet()
  const setActiveSourceIds = useWikiCitationSetActiveSourceIds()
  const openSourceViewer = useWikiOpenSourceViewer()

  const firstSourceId = sourceIds[0]
  const source = firstSourceId != null ? sources[firstSourceId] : null
  const file =
    source?.source_type === 'file'
      ? (filesById.get(source.file_id) ?? null)
      : null

  const name = getCitationDisplayName({
    source,
    file,
    deletedFileIds,
    sourceFileNameById,
  })

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      props.onClick?.(e)
      if (!source) return

      switch (source.source_type) {
        case 'web': {
          const win = window.open(source.url, '_blank', 'noopener,noreferrer')
          if (win) win.opener = null
          return
        }
        case 'file':
          if (deletedFileIds.has(source.file_id)) return
          setActiveSourceIds(sourceIds)
          openSourceViewer()
          return
        case 'entity':
        case 'image':
        case 'tool':
          return
      }
    },
    [
      source,
      sourceIds,
      props.onClick,
      deletedFileIds,
      setActiveSourceIds,
      openSourceViewer,
    ],
  )

  if (!name) {
    return <NoopButton ref={ref} {...props} />
  }

  return (
    <CitationTriggerWrapper
      ref={ref}
      {...props}
      onClick={handleClick}
      idRange={idRange}
      className="text-xs !py-1.5 px-1.5"
    >
      {name}
    </CitationTriggerWrapper>
  )
})

WikiFirstCitationName.displayName = 'Wiki.FirstCitationName'

interface CitationItemProps {
  source: Source | undefined | null
  file: File | undefined | null
  sourceId: number
  onSourceClick?: (id: number) => void
  isLoading?: boolean
  isDeleted?: boolean
  sourceIdsOverride?: number[]
  labelOverride?: string
}

const CitationItem: React.FC<CitationItemProps> = ({
  source,
  file,
  sourceId,
  onSourceClick,
  isLoading = false,
  isDeleted = false,
  sourceIdsOverride,
  labelOverride,
}) => {
  const setActiveSourceIds = useWikiCitationSetActiveSourceIds()
  const openSourceViewer = useWikiOpenSourceViewer()

  const handleClick = useCallback(() => {
    if (!source || isDeleted) return

    switch (source.source_type) {
      case 'web': {
        const win = window.open(source.url, '_blank', 'noopener,noreferrer')
        if (win) win.opener = null
        return
      }
      case 'file':
        setActiveSourceIds(sourceIdsOverride ?? [sourceId])
        openSourceViewer()
        onSourceClick?.(sourceId)
        return
      case 'entity':
      case 'image':
      case 'tool':
        return
    }
  }, [
    source,
    isDeleted,
    sourceId,
    sourceIdsOverride,
    setActiveSourceIds,
    openSourceViewer,
    onSourceClick,
  ])

  const metadata = useMemo(() => formatFileCitationMetadata(source), [source])

  const renderMetadata = useMemo(() => {
    if (metadata.length === 0) return null

    return (
      <div className="flex items-center gap-1 text-xs text-primary-500 dark:text-primary-400 mt-0.5">
        {metadata.map((item, idx) => (
          <React.Fragment key={idx}>
            {idx > 0 && <span className="mx-1">•</span>}
            <span>{item}</span>
          </React.Fragment>
        ))}
      </div>
    )
  }, [metadata])

  if (isLoading || !source) {
    return (
      <div className="w-full flex items-center justify-center px-2 py-1.5">
        <Spinner className="w-3 h-3" />
      </div>
    )
  }

  if (isDeleted && source.source_type === 'file') {
    const deletedLabel = cleanFileName(source.file_name ?? 'Deleted file')

    return (
      <div className="w-full flex flex-col gap-0.5 items-start px-2 py-1.5 rounded-md text-left opacity-80 cursor-default">
        <div className="flex items-center justify-between gap-1.5 w-full">
          <div className="flex items-center gap-1.5 flex-1 min-w-0">
            <div className="text-primary-400 dark:text-primary-500 flex-shrink-0">
              <CompactThumbnail name={deletedLabel} size={12} />
            </div>
            <span className="text-sm text-primary-500 dark:text-primary-400 truncate">
              {deletedLabel}
            </span>
          </div>
          <span className="text-[13px] text-primary-500 flex-shrink-0">
            {labelOverride ?? `[${sourceId}]`}
          </span>
        </div>
        {renderMetadata}
      </div>
    )
  }

  return (
    <BaseCitationItem
      source={source}
      file={file ?? null}
      sourceId={sourceId}
      onClick={handleClick}
      labelOverride={labelOverride}
    >
      {renderMetadata}
    </BaseCitationItem>
  )
}

interface WikiSourceCitationItemProps {
  sourceId: number
  onSourceClick?: (id: number) => void
  sourceIdsOverride?: number[]
  labelOverride?: string
}

const WikiSourceCitationItem = memo(
  ({
    sourceId,
    onSourceClick,
    sourceIdsOverride,
    labelOverride,
  }: WikiSourceCitationItemProps) => {
    const sources = useWikiCitationSources()
    const filesById = useWikiCitationFilesById()
    const sourceFileNameById = useWikiCitationSourceFileNameById()
    const deletedFileIds = useWikiDeletedSourceFileIdSet()
    const pursuitFilesLoaded = useWikiCitationPursuitFilesLoaded()

    const source = sources[sourceId]
    const isDeleted =
      source?.source_type === 'file' &&
      isDeletedWikiSourceFile(source.file_id, deletedFileIds)

    const file =
      source?.source_type === 'file'
        ? (filesById.get(source.file_id) ?? null)
        : null

    const isLoading =
      !isDeleted &&
      source?.source_type === 'file' &&
      !pursuitFilesLoaded &&
      !hasStoredWikiFileLabel(source, sourceFileNameById)

    return (
      <CitationItem
        source={source}
        file={file}
        sourceId={sourceId}
        onSourceClick={onSourceClick}
        isLoading={isLoading}
        isDeleted={isDeleted}
        sourceIdsOverride={sourceIdsOverride}
        labelOverride={labelOverride}
      />
    )
  },
)

WikiSourceCitationItem.displayName = 'Wiki.SourceCitationItem'

interface DeletedCitationBadgeProps extends React.ComponentPropsWithoutRef<'span'> {
  label: string
  idRange: string
}

const DeletedCitationBadge = forwardRef<
  HTMLSpanElement,
  DeletedCitationBadgeProps
>(({ label, idRange, className, ...props }, ref) => {
  return (
    <span
      ref={ref}
      aria-label={`${label} (deleted) ${idRange}`}
      className={[
        'inline-flex items-center justify-center font-medium gap-1 h-[17px] px-[5px] py-[1px] rounded-full text-[11px] bg-primary-100 dark:bg-primary-900 ring-1 ring-primary-200 dark:ring-primary-800 text-primary-400 dark:text-primary-600 cursor-default select-none',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...props}
    >
      <span className="truncate max-w-[84px]">{label}</span>
      <span className="flex-shrink-0">{idRange}</span>
    </span>
  )
})

DeletedCitationBadge.displayName = 'Wiki.DeletedCitationBadge'

interface WikiSourceCitationGroupProps {
  group: CitationGroup
}

const WikiSourceCitationGroup: React.FC<WikiSourceCitationGroupProps> = ({
  group,
}) => {
  const sources = useWikiCitationSources()
  const deletedFileIds = useWikiDeletedSourceFileIdSet()

  const idRange = formatIdRange(group.ids)
  const shouldGroup = group.sourceType === 'file' && group.ids.length > 1
  const firstSource = group.ids[0] != null ? sources[group.ids[0]] : null

  const isDeletedFile =
    firstSource?.source_type === 'file' &&
    isDeletedWikiSourceFile(firstSource.file_id, deletedFileIds)

  if (isDeletedFile) {
    return (
      <span className="mx-0.5 inline-block align-top">
        <Tooltip>
          <TooltipTrigger asChild>
            <DeletedCitationBadge
              label={cleanFileName(firstSource.file_name ?? 'Deleted file')}
              idRange={idRange}
            />
          </TooltipTrigger>
          <TooltipContent>This file was deleted.</TooltipContent>
        </Tooltip>
      </span>
    )
  }

  return (
    <BaseSourceCitation
      ids={group.ids}
      trigger={
        <WikiFirstCitationName sourceIds={group.ids} idRange={idRange} />
      }
    >
      {shouldGroup ? (
        <WikiSourceCitationItem
          sourceId={group.ids[0]}
          sourceIdsOverride={group.ids}
          labelOverride={idRange}
        />
      ) : (
        group.ids.map((id) => <WikiSourceCitationItem key={id} sourceId={id} />)
      )}
    </BaseSourceCitation>
  )
}

interface SourceCitationHtmlProps {
  'data-id'?: string
  'data-ids'?: string
}

export const WikiSourceCitation: React.FC<SourceCitationHtmlProps> = ({
  'data-id': dataId,
  'data-ids': dataIds,
}) => {
  const sources = useWikiCitationSources()

  const ids = useMemo(() => {
    if (dataIds) {
      return dataIds
        .split(',')
        .map((id) => parseInt(id.trim(), 10))
        .filter((id) => !isNaN(id))
    }
    if (dataId) {
      const id = parseInt(dataId, 10)
      return isNaN(id) ? [] : [id]
    }
    return []
  }, [dataId, dataIds])

  const badgeGroups = useMemo(
    () => groupConsecutiveCitationsByFile(ids, sources),
    [ids, sources],
  )

  if (ids.length === 0) return null

  return (
    <>
      {badgeGroups.map((group, groupIndex) => (
        <WikiSourceCitationGroup
          key={`group-${groupIndex}-${group.ids[0]}`}
          group={group}
        />
      ))}
    </>
  )
}

WikiSourceCitation.displayName = 'Wiki.SourceCitation'
